import { RefreshCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@resource-kit/ui/react';
import { http } from '@lib/api';
import { queryKeys } from '@lib/queryKeys';
import type { components } from '@/types/api.generated';
import { ReconcileCheckCard } from '../components/ReconcileCheckCard';
import { TruthReconcileCard } from '../components/TruthReconcileCard';

type ReconcileReport = components['schemas']['ReconcileReport'];
type TruthReconcileReport = components['schemas']['DispatchTruthReconcileReport'];
type RecentOutcomes = components['schemas']['RecentDeliveryOutcomes'];

export interface DeliveredPaneProps {
  date: string;
  onReconcile: () => void;
  reconcilePending: boolean;
  truth: {
    triggered: boolean;
    report: TruthReconcileReport | undefined;
    isFetching: boolean;
    isError: boolean;
    error: unknown;
  };
  reconcile: {
    triggered: boolean;
    report: ReconcileReport | undefined;
    error: unknown;
    isPending: boolean;
  };
}

const CARD = 'rounded-xl bg-surface-1 ring-1 ring-inset ring-border/60';
const DASH = '—';

/**
 * The delivered pane (#2484): three KPI cards from one read of
 * `GET /api/dispatch/recent-outcomes`, the compact reconcile card (the reconcile
 * queries stay on the page so the result survives switching cells) and the
 * "this batch" note. Pure read; the reconcile button reuses the page's single
 * reconcile action.
 */
export function DeliveredPane({
  date,
  onReconcile,
  reconcilePending,
  truth,
  reconcile,
}: DeliveredPaneProps) {
  const { t } = useTranslation();
  const outcome = useQuery({
    queryKey: queryKeys.dispatch.dayOutcome(date),
    queryFn: () =>
      http.get<RecentOutcomes>('/api/dispatch/recent-outcomes', {
        params: { to: date, count: 1 },
      }),
    staleTime: 60_000,
  });
  // The endpoint returns the latest day <= `date` that has jobs; a different
  // day is not this cell's numbers, so it reads as a failed read.
  const first = outcome.data?.rows[0];
  const row = first?.shipping_date === date ? first : undefined;
  const num = (value: number | undefined) => (value === undefined ? DASH : String(value));
  const neverChecked = !truth.triggered && !reconcile.triggered;

  return (
    <div
      data-testid="dispatch-pane-delivered"
      className="grid items-start gap-6 md:grid-cols-[minmax(0,1fr)_340px]"
    >
      <section className="flex flex-col gap-2.5">
        <div className="grid gap-2.5 md:grid-cols-3">
          <div data-testid="dispatch-delivered-kpi-delivered" className={`${CARD} px-4 py-3.5`}>
            <div className="text-[12.5px] text-muted-foreground">
              {t('dispatch.delivered.kpi.delivered')}
            </div>
            <div className="text-[26px] font-display font-bold leading-[1.15] tracking-[-0.02em] tabular-nums text-foreground">
              {num(row?.delivered)}
            </div>
            <div className="text-[12.5px] text-muted-foreground">
              {row ? t('dispatch.delivered.kpi.ofTotal', { total: row.total }) : ''}
            </div>
          </div>
          <div data-testid="dispatch-delivered-kpi-late" className={`${CARD} px-4 py-3.5`}>
            <div className="text-[12.5px] text-muted-foreground">
              {t('dispatch.delivered.kpi.late', { at: row?.window_end_local ?? DASH })}
            </div>
            <div className="text-[26px] font-display font-bold leading-[1.15] tracking-[-0.02em] tabular-nums text-foreground">
              {num(row?.delivered_late)}
            </div>
            <div className="text-[12.5px] text-muted-foreground">
              {t('dispatch.delivered.kpi.lateHint')}
            </div>
          </div>
          <div data-testid="dispatch-delivered-kpi-written-back" className={`${CARD} px-4 py-3.5`}>
            <div className="text-[12.5px] text-muted-foreground">
              {t('dispatch.delivered.kpi.writtenBack')}
            </div>
            <div className="text-[26px] font-display font-bold leading-[1.15] tracking-[-0.02em] tabular-nums">
              {num(row?.written_back)}
            </div>
            <div className="text-[12.5px] text-muted-foreground">
              {t('dispatch.delivered.kpi.writtenBackHint')}
            </div>
          </div>
        </div>
        <div data-testid="dispatch-delivered-reconcile" className={`${CARD} overflow-hidden`}>
          <div className="flex items-center gap-2.5 bg-surface-2 px-4 py-[11px]">
            <span className="flex-1 text-[14.5px] font-semibold">
              {t('dispatch.delivered.reconcile.title')}
            </span>
            <Button
              data-testid="dispatch-reconcile-run"
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-[12.5px]"
              onClick={onReconcile}
              disabled={reconcilePending}
            >
              <RefreshCw
                className={`mr-1.5 h-3.5 w-3.5 ${reconcilePending ? 'animate-spin' : ''}`}
              />
              {reconcilePending
                ? t('dispatch.reconcileAll.running')
                : neverChecked
                  ? t('dispatch.delivered.reconcile.runFirst')
                  : t('dispatch.delivered.reconcile.runAgain')}
            </Button>
          </div>
          <TruthReconcileCard
            triggered={truth.triggered}
            report={truth.report}
            isFetching={truth.isFetching}
            isError={truth.isError}
            error={truth.error}
          />
          <ReconcileCheckCard
            date={date}
            triggered={reconcile.triggered}
            report={reconcile.report}
            error={reconcile.error}
            isPending={reconcile.isPending}
          />
        </div>
      </section>
      <aside data-testid="dispatch-delivered-note" className={`${CARD} flex flex-col gap-3 p-4`}>
        <div className="text-[12.5px] text-muted-foreground">
          {t('dispatch.delivered.note.title')}
        </div>
        <p className="text-sm">{t('dispatch.delivered.note.body')}</p>
      </aside>
    </div>
  );
}
